/**
 * Email Template for Monthly Fuel Trend Analysis Reports
 * 
 * Generates HTML email body with dynamic placeholders for KPIs and filters.
 * Images (logo and banner) are referenced as CID attachments.
 */

/**
 * Generate the email HTML body
 * @param {Object} data - Template data
 * @param {string} data.monthYear - Report month/year (e.g., "January 2026")
 * @param {string} data.clientName - Recipient name (will be replaced per-recipient by server)
 * @param {number} data.totalAssets - Total assets analyzed
 * @param {string} data.fuelConsumedL - Fuel consumed in litres (formatted)
 * @param {string} data.fuelConsumedMoM - MoM change for fuel consumed
 * @param {number} data.totalRefills - Total refill count
 * @param {string} data.totalRefillsMoM - MoM change for refills
 * @param {number} data.criticalHighCount - Count of Critical + High severity assets
 * @param {string} data.bookMeetingLink - Link for booking a review call (optional)
 * @param {string} data.websiteUrl - Website URL (optional)
 * @param {string} data.filtersDescription - Description of active filters (optional)
 * @returns {string} HTML email body
 */
export const generateEmailHTML = (data) => {
  const {
    monthYear = 'Month Year',
    clientName = '{{CLIENT_NAME}}', // Placeholder replaced per-recipient by server
    totalAssets = 0,
    fuelConsumedL = '0',
    fuelConsumedMoM = 'N/A',
    totalRefills = 0,
    totalRefillsMoM = 'N/A',
    criticalHighCount = 0,
    bookMeetingLink = 'mailto:rishabhberia@gmail.com?subject=Review%20Call%20Request',
    websiteUrl = 'https://theassetpro.com',
    filtersDescription = ''
  } = data;

  // Build filters section if filters are active
  const filtersSection = filtersDescription ? `
    <tr>
      <td style="padding:0 28px 16px 28px;">
        <div style="border:1px solid #fef3c7; border-radius:8px; padding:12px; background:#fffbeb;">
          <div style="font-size:12px; font-weight:700; color:#92400e; letter-spacing:0.3px;">REPORT FILTERS APPLIED</div>
          <div style="margin-top:6px; font-size:13px; color:#78350f;">${filtersDescription}</div>
        </div>
      </td>
    </tr>
  ` : '';

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <meta name="x-apple-disable-message-reformatting" />
  <title>Monthly Fuel Trend Analysis</title>
</head>

<body style="margin:0; padding:0; background:#f5f7fb; font-family:-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; color:#0f172a;">
  <!-- Preheader (hidden preview text) -->
  <div style="display:none; font-size:1px; color:#f5f7fb; line-height:1px; max-height:0; max-width:0; opacity:0; overflow:hidden;">
    Your Monthly Fuel Trend Analysis report is ready — highlights, exception assets, and recommended actions inside.
  </div>

  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#f5f7fb;">
    <tr>
      <td align="center" style="padding:28px 16px;">
        <!-- Container -->
        <table role="presentation" width="680" cellspacing="0" cellpadding="0" border="0" style="width:680px; max-width:100%; background:#ffffff; border-radius:14px; overflow:hidden; box-shadow:0 10px 28px rgba(15,23,42,0.08);">
          
          <!-- Header -->
          <tr>
            <td style="padding:26px 28px 14px 28px; background:linear-gradient(180deg,#0b1b3a 0%, #102a5f 100%);">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
                <tr>
                  <td align="left" valign="middle">
                    <!-- AssetPro Logo -->
                    <img
                      src="https://theassetpro.com/assets/img/main-logo.png"
                      alt="AssetPro"
                      width="140"
                      style="display:block; height:auto; border:0; outline:none; text-decoration:none;"
                    />
                  </td>
                  <td align="right" valign="middle" style="color:#e2e8f0; font-size:12px; letter-spacing:0.2px;">
                    <div style="font-weight:600; color:#ffffff; font-size:14px;">Monthly Report</div>
                    <div style="opacity:0.9;">${monthYear}</div>
                  </td>
                </tr>
              </table>

              <div style="margin-top:14px; color:#ffffff; font-size:22px; font-weight:800; line-height:1.2;">
                Fuel Trend Analysis
              </div>
              <div style="margin-top:8px; color:#cbd5e1; font-size:14px; line-height:1.55;">
                A quick monthly view of fuel consumption, efficiency trends, and exception assets across your fleet.
              </div>
            </td>
          </tr>

          <!-- Body -->
          <tr>
            <td style="padding:22px 28px 10px 28px;">
              <div style="font-size:14px; line-height:1.7; color:#0f172a;">
                Dear ${clientName},
                <br /><br />
                Please find attached the <strong>Fuel Trend Analysis Report for ${monthYear}</strong>.
                Below is a concise snapshot to help you focus on the highest-impact areas before reviewing the full report.
              </div>
            </td>
          </tr>

          ${filtersSection}

          <!-- Banner Image (middle) -->
          <tr>
            <td align="center" style="padding:12px 28px 20px 28px;">
              <img
                src="https://theassetpro.com/media/monthy_fuel_trend_analysis.png"
                alt="Monthly Fuel Trend Analysis"
                width="624"
                style="display:block; width:100%; max-width:624px; height:auto; border-radius:12px; border:1px solid #e5e7eb;"
              />
            </td>
          </tr>

          <!-- KPI Cards -->
          <tr>
            <td style="padding:0 28px 16px 28px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
                <tr>
                  <!-- Card 1 -->
                  <td width="50%" style="padding:0 8px 12px 0;">
                    <div style="border:1px solid #e5e7eb; border-radius:12px; padding:14px; background:#fbfdff;">
                      <div style="font-size:12px; color:#64748b; font-weight:700; letter-spacing:0.3px;">FLEET COVERAGE</div>
                      <div style="margin-top:6px; font-size:20px; font-weight:800; color:#0f172a;">${totalAssets}</div>
                      <div style="margin-top:4px; font-size:12px; color:#64748b;">Assets analysed</div>
                    </div>
                  </td>

                  <!-- Card 2 -->
                  <td width="50%" style="padding:0 0 12px 8px;">
                    <div style="border:1px solid #e5e7eb; border-radius:12px; padding:14px; background:#fbfdff;">
                      <div style="font-size:12px; color:#64748b; font-weight:700; letter-spacing:0.3px;">FUEL CONSUMED</div>
                      <div style="margin-top:6px; font-size:20px; font-weight:800; color:#0f172a;">${fuelConsumedL} L</div>
                      <div style="margin-top:4px; font-size:12px; color:#64748b;">MoM: ${fuelConsumedMoM}</div>
                    </div>
                  </td>
                </tr>

                <tr>
                  <!-- Card 3 -->
                  <td width="50%" style="padding:0 8px 0 0;">
                    <div style="border:1px solid #e5e7eb; border-radius:12px; padding:14px; background:#fbfdff;">
                      <div style="font-size:12px; color:#64748b; font-weight:700; letter-spacing:0.3px;">REFILL ACTIVITY</div>
                      <div style="margin-top:6px; font-size:20px; font-weight:800; color:#0f172a;">${totalRefills}</div>
                      <div style="margin-top:4px; font-size:12px; color:#64748b;">MoM: ${totalRefillsMoM}</div>
                    </div>
                  </td>

                  <!-- Card 4 -->
                  <td width="50%" style="padding:0 0 0 8px;">
                    <div style="border:1px solid #e5e7eb; border-radius:12px; padding:14px; background:#fbfdff;">
                      <div style="font-size:12px; color:#64748b; font-weight:700; letter-spacing:0.3px;">PRIORITY FLAGS</div>
                      <div style="margin-top:6px; font-size:20px; font-weight:800; color:#0f172a;">${criticalHighCount}</div>
                      <div style="margin-top:4px; font-size:12px; color:#64748b;">Critical / High severity assets</div>
                    </div>
                  </td>
                </tr>
              </table>

              <div style="margin-top:14px; font-size:13px; color:#64748b; line-height:1.6;">
                Tip: Start with <strong>Critical</strong> and <strong>High</strong> assets — these typically offer the fastest opportunities for operational correction, maintenance prioritisation, and fuel-cost control.
              </div>
            </td>
          </tr>

          <!-- Insights + Actions -->
          <tr>
            <td style="padding:0 28px 18px 28px;">
              <div style="border:1px solid #e5e7eb; border-radius:12px; padding:16px; background:#ffffff;">
                <div style="font-size:14px; font-weight:800; color:#0f172a;">What this report helps you do</div>
                <ul style="margin:10px 0 0 18px; padding:0; color:#0f172a; line-height:1.7; font-size:14px;">
                  <li>Detect early signs of abnormal fuel behaviour (inefficiency, wastage, possible leakage).</li>
                  <li>Prioritise interventions based on highest variance and impact assets.</li>
                  <li>Track month-on-month improvement against consistent benchmarks.</li>
                </ul>
              </div>
            </td>
          </tr>

          <!-- CTA Button -->
          <tr>
            <td style="padding:0 28px 24px 28px;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td align="left">
                    <a
                      href="${bookMeetingLink}"
                      style="display:inline-block; background:#2563eb; color:#ffffff; text-decoration:none; font-weight:800; font-size:14px; padding:12px 16px; border-radius:10px;"
                    >
                      Book a 20-min review call
                    </a>
                    <div style="margin-top:10px; font-size:13px; color:#64748b; line-height:1.6;">
                      If you prefer, reply to this email with a suitable time window and we'll coordinate.
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding:18px 28px; background:#f8fafc; border-top:1px solid #e5e7eb;">
              <div style="font-size:14px; color:#0f172a; line-height:1.7;">
                Warm regards,<br />
                <strong>Rishabh Beria</strong><br />
                AssetPro
              </div>
              <div style="margin-top:8px; font-size:12px; color:#64748b; line-height:1.6;">
                Email: <a href="mailto:rishabhberia@gmail.com" style="color:#2563eb; text-decoration:none;">rishabhberia@gmail.com</a>
                &nbsp;&bull;&nbsp;
                Website: <a href="${websiteUrl}" style="color:#2563eb; text-decoration:none;">${websiteUrl}</a>
              </div>

              <div style="margin-top:10px; font-size:11px; color:#94a3b8; line-height:1.6;">
                You are receiving this email as part of your monthly fleet analytics reporting.
              </div>
            </td>
          </tr>

        </table>
        <!-- /Container -->
      </td>
    </tr>
  </table>
</body>
</html>`;
};

/**
 * Generate the email subject line
 * @param {string} monthYear - Report month/year (e.g., "January 2026")
 * @param {string} filtersDescription - Description of active filters (optional)
 * @returns {string} Email subject
 */
export const generateEmailSubject = (monthYear, filtersDescription = '') => {
  let subject = `Monthly Fuel Trend Analysis - ${monthYear}`;
  if (filtersDescription) {
    subject += ` (${filtersDescription})`;
  }
  return subject;
};

/**
 * Format filters for display in email
 * @param {Array} activeFilters - Array of active filter objects { label, value }
 * @returns {string} Formatted filters description
 */
export const formatFiltersForEmail = (activeFilters) => {
  if (!activeFilters || activeFilters.length === 0) {
    return '';
  }
  return activeFilters
    .map(f => `${f.label}: ${f.value}`)
    .join(' | ');
};

export default {
  generateEmailHTML,
  generateEmailSubject,
  formatFiltersForEmail
};
